import { Link } from 'react-scroll';
import { useEffect, useRef, useState } from 'react';
import { useLayoutBreakpoint } from '@hooks/use-layout-breakpoint';
import styles from './navbar.module.css';
import { NUMBERS } from './numbers';
import Arrow from '@svg/chevron-up.svg';

const NavbarItem = ({
  file,
  isActive,
  title,
  target,
  onClick = () => {},
  onSetActive = () => {}
}) => {
  return file ? (
    <a
      href={file || ''}
      className={`${styles.anchor} snf-color-white`}
      target="_blank"
      rel="noreferrer"
    >
      {title || ''}
    </a>
  ) : (
    <Link
      spy
      onClick={onClick}
      style={isActive ? { borderBottom: '2px solid white' } : null}
      to={target || ''}
      className={`${styles.anchor} snf-color-white`}
      duration={500}
      id={`${title}_${target}`}
      onSetActive={onSetActive}
      itemRef={target}
      isDynamic
    >
      {title || ''}
    </Link>
  );
};

const NavbarItems = ({ navbarItems = [], activeElement, isFixed, onClick, onSetActive }) => {
  return navbarItems?.map((element, index) => (
    <div className={`col ${styles.navbarItem}`} style={{ maxHeight: 100 }} key={`navbar-${index}`}>
      <NavbarItem
        file={element.file}
        target={element.target}
        title={element.title}
        isActive={isFixed ? activeElement?.target === element.target : false}
        onClick={() => onClick(element)}
        onSetActive={() => onSetActive(element)}
      />
    </div>
  ));
};

const Navbar = ({
  navbarItems = [],
  selectedNavItem = { isOpen: false },
  onNavItemClick,
  ...rest
}) => {
  const [isFixed, setIsFixed] = useState(false);
  const itemsRef = useRef();
  const breakpoint = useLayoutBreakpoint();
  const isMobile = ['xs', 'sm', 'md'].indexOf(breakpoint) >= 0;

  const handleScroll = () => {
    const offset = window.scrollY;
    const displayHeight = window.innerHeight;
    const windowHeight = (displayHeight * NUMBERS.NINETY) / NUMBERS.ONEHUNDRED;

    if (offset > windowHeight) {
      return setIsFixed(true);
    }
    return setIsFixed(false);
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    const { hash } = window.location;
    if (hash) {
      onNavItemClick({ isOpen: false, item: navbarItems.find((item) => item.title === hash) });
    }
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    console.log('I am clicked hello');
    onNavItemClick({ isOpen: false });
  }, [navbarItems]);

  if (isMobile) {
    const itemsMaxHeight = selectedNavItem.isOpen
      ? `${itemsRef.current?.scrollHeight ?? 0}px`
      : '0';

    return (
      <>
        <div
          id="navbar"
          className={`${styles.navbar} ${styles.mobile}
      snf-font-16 snf-font-w6 row  justify-content-between p-0 px-5 w-100
      ${isFixed ? 'position-fixed' : 'position-absolute'}`}
          style={{
            '--navbar-mobile-bottom': isFixed ? 'unset' : '0',
            '--navbar-mobile-top': isFixed ? '0' : 'unset',
            '--navbar-items-max-height': itemsMaxHeight,
            '--navbar-arrow-transform': !selectedNavItem.isOpen ? 'rotate(180deg)' : 'rotate(0deg)'
          }}
        >
          <div
            className={`row w-100 justify-content-between align-items-center ${styles.divItem}`}
            role="button"
            tabIndex="0"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onNavItemClick((oldState) => ({ ...oldState, isOpen: !oldState.isOpen }));
            }}
            onKeyDown={(event) => event.target.click()}
          >
            <div className={`py-3 text-truncate ${styles.truncateNav}`}>
              <NavbarItem
                target={selectedNavItem.item?.target || navbarItems[0].target}
                title={selectedNavItem.item?.title || navbarItems[0].title}
                isActive={isFixed}
                onClick={(event) => {
                  event.stopPropagation();
                  onNavItemClick((oldState) => ({ ...oldState, isOpen: !oldState.isOpen }));
                }}
              />
            </div>
            <div className={styles.arrowDiv}>
              <Arrow width="1rem" height="1rem" />
            </div>
          </div>
          <div className="row w-100">
            <div ref={itemsRef} className={styles.divItems}>
              {navbarItems
                .filter((element, index) =>
                  !isFixed ? index !== 0 : element.title !== selectedNavItem.item?.title
                )
                ?.map((element, index) => (
                  <div
                    className={`py-3 ${styles.divItem} text-truncate ${styles.truncateNav}`}
                    key={`navbar-${index}`}
                    style={{ maxHeight: 40 }}
                  >
                    <NavbarItem
                      onClick={() => {
                        onNavItemClick({
                          item: element,
                          isOpen: false
                        });
                      }}
                      onSetActive={() => {
                        onNavItemClick((oldState) => ({
                          ...oldState,
                          item: element
                        }));
                      }}
                      file={element.file}
                      target={element.target}
                      title={element.title}
                    />
                  </div>
                ))}
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div
        className={`${styles.navbar} ${styles.desktop}

      snf-font-12 snf-font-lg-16 snf-font-w6 row justify-content-between p-0
      ${isFixed ? 'position-fixed w-100' : ''}`}
        style={{
          '--navbar-desktop-bottom': isFixed ? 'unset' : '0',
          '--navbar-desktop-top': isFixed ? '0' : 'unset',
          maxHeight: 100
        }}
        {...rest}
      >
        <NavbarItems
          navbarItems={navbarItems}
          activeElement={selectedNavItem.item}
          isFixed={isFixed}
          onClick={(element) => {
            onNavItemClick((oldState) => ({ ...oldState, item: element }));
          }}
          onSetActive={(element) => {
            onNavItemClick((oldState) => ({ ...oldState, item: element }));
          }}
        />
      </div>
    </>
  );
};

Navbar.displayName = 'Navbar';
export { Navbar };
